import { useState, useEffect } from 'react';
import { Dialog as PrimeDialog } from 'primereact/dialog';
import { Dialog, Typography } from '@uni-of-exeter/digital.lib.react.ui.kit';

import './error-banner.css';
import { BannerError } from './types';
import { APP_TEXT } from '../../const/app-text';
import { fDateTime } from '../../utils/format-time';
import { useBreakpoint } from '../../hooks/utils/use-breakpoint';

interface ErrorBannerProps {
  open: boolean;
  onClose: () => void;
  error: BannerError;
}

export function ErrorBanner({ open, onClose, error }: ErrorBannerProps) {
  const isMobile = useBreakpoint('md', 'down');
  const [showDetails, setShowDetails] = useState(false);

  const errorDescription = error.estimatedFixTime
    ? APP_TEXT.components.errorBanner.descriptionTemplate.withTime
        .replace('{message}', error.message)
        .replace('{time}', fDateTime(error.estimatedFixTime))
    : APP_TEXT.components.errorBanner.descriptionTemplate.withoutTime.replace(
        '{message}',
        error.message
      );

  useEffect(() => {
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.style.filter = showDetails ? 'blur(4px)' : '';
    }

    return () => {
      if (rootElement) {
        rootElement.style.filter = '';
      }
    };
  }, [showDetails]);

  const handleShowDetails = () => setShowDetails(true);
  const handleCloseDetails = () => setShowDetails(false);

  return (
    <>
      {open && <div className="p-error-banner-space" />}

      <PrimeDialog
        visible={open}
        onHide={onClose}
        position="bottom"
        dismissableMask
        modal={false}
        draggable={false}
        resizable={false}
        showHeader={false}
        className="p-error-banner"
      >
        <div className="p-error-banner-container">
          <div className="p-error-banner__content">
            <Typography
              variant="subtitle"
              fontWeight={isMobile ? 500 : 700}
              fontSize={isMobile ? '0.825rem' : undefined}
              className="p-error-banner__title"
            >
              {APP_TEXT.components.errorBanner.title}
              {isMobile && (
                <div
                  className="p-error-banner__info-icon"
                  onClick={handleShowDetails}
                  data-testid="info-icon"
                >
                  <i className="fa-solid fa-info-circle resourceTableHeaderIcon" />
                </div>
              )}
            </Typography>
            {!isMobile && <Typography variant="body.small">{errorDescription}</Typography>}
          </div>
        </div>
      </PrimeDialog>

      {isMobile && (
        <Dialog
          open={showDetails}
          onClose={handleCloseDetails}
          footer={<></>}
        >
          <Typography variant="body.small">{errorDescription}</Typography>
        </Dialog>
      )}
    </>
  );
}
